import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { navigate } from "wouter/use-browser-location";

import { cn } from "@/lib/utils";
import { PillTabs } from "@/components/v2/layout/PillTabs";
import {
  type ProjectTabId,
  visibleProjectTabsForStatus,
} from "@/pages/v2/projectTabs";

interface ProjectTabsProps {
  /** Project UUID — used for lookups / Shell identity once resolved from {@link projectShortId}. */
  projectUuid: string;
  /** Canonical slug — used for **all** tab URLs (`/projects/:shortId/overview|research|…`). */
  projectShortId?: string;
  activeTab: ProjectTabId;
  onTabChange?: (tab: ProjectTabId) => void;
  /**
   * Current `projects.status`. Drives status-aware tab visibility: an
   * early-lifecycle project hides the not-yet-populated tabs (e.g. Research
   * until spec 040). Omitted → the full strip (back-compat for callers that
   * don't yet thread status).
   */
  projectStatus?: string | null;
  className?: string;
}

/**
 * Builds the project-tab route path (leading `/`).
 */
export function projectDashboardPath(
  projectUuid: string,
  projectShortId: string | undefined,
  tab: ProjectTabId,
): string {
  const slug = projectShortId ?? projectUuid;
  if (tab === "overview") {
    return `/projects/${slug}/overview`;
  }
  return `/projects/${slug}/${tab}`;
}

export function ProjectTabs({
  projectUuid,
  projectShortId,
  activeTab,
  onTabChange,
  projectStatus,
  className,
}: ProjectTabsProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const tabs = visibleProjectTabsForStatus(projectStatus);
  const activeTabData = tabs.find((t) => t.id === activeTab);
  const ActiveIcon = activeTabData?.icon;

  function handleTabChange(tab: ProjectTabId) {
    onTabChange?.(tab);
    setMobileOpen(false);
    navigate(projectDashboardPath(projectUuid, projectShortId, tab));
  }

  return (
    <nav
      className={cn(
        // No glass-nav background here: the tab group (TabsList pill) carries
        // its own surface and floats directly on the page background/grid.
        "flex items-center justify-center py-1.5",
        className,
      )}
    >
      {/* Desktop: horizontal pill tabs. `overflow-x-auto` (inside PillTabs)
          lets the 7-tab strip scroll horizontally on tighter tablet widths
          (≈768–1024px) instead of clipping behind the user-menu /
          project-picker chrome. */}
      <PillTabs
        tabs={tabs}
        value={activeTab}
        onChange={(v) => handleTabChange(v)}
        className="hidden md:block"
      />

      {/* Mobile: collapsible dropdown matching the handoff design.
          - Trigger is a card-style button (subtle surface + blur), with
            the icon on the left, label left-aligned in the middle, and
            chevron right.
          - Panel is an in-flow card (solid surface, rounded, bordered,
            shadowed) — NOT a floating backdrop-blurred overlay.
          - Items render label-first then icon, centred as a group.
            Active item gets the primary gradient. */}
      <div className="flex w-full flex-col gap-1.5 px-3.5 pt-3 md:hidden">
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-xl border border-transparent bg-card/60 px-3.5 py-2.5 text-left text-[13px] font-semibold text-foreground backdrop-blur-sm transition-colors hover:bg-card"
          onClick={() => setMobileOpen((o) => !o)}
        >
          {ActiveIcon && <ActiveIcon className="size-4 shrink-0 text-foreground" />}
          <span className="flex-1 truncate">{activeTabData?.label}</span>
          {mobileOpen ? (
            <ChevronUp className="size-3.5 text-muted-foreground" />
          ) : (
            <ChevronDown className="size-3.5 text-muted-foreground" />
          )}
        </button>

        {mobileOpen && (
          <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-[0_8px_24px_rgba(0,0,0,0.18)]">
            {tabs.map((tab, i) => {
              const Icon = tab.icon;
              const isActive = tab.id === activeTab;
              const isLast = i === tabs.length - 1;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={cn(
                    "flex items-center justify-center gap-2.5 px-3.5 py-3 text-center text-[13px] font-medium transition-colors",
                    !isLast && "border-b border-border/40",
                    isActive
                      ? "font-semibold text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                  style={
                    isActive
                      ? {
                          background: "var(--gradient-primary)",
                          color: "var(--primary-foreground)",
                        }
                      : undefined
                  }
                  onClick={() => handleTabChange(tab.id)}
                >
                  <Icon className="size-3.5 shrink-0" />
                  <span className="shrink-0">{tab.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
}
